import { memo } from 'react';

import { cn } from '../../lib/styles';

export interface PageHeaderProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'title'> {
  title: React.ReactNode;
  titleClassName?: string;
  titleSuffix?: React.ReactNode;
  left?: React.ReactNode;
  right?: React.ReactNode;
  description?: React.ReactNode;
  descriptionClassName?: string;
}

export const PageHeaderTitle = ({
  children,
  className,
  suffix,
}: {
  children: React.ReactNode;
  className?: string;
  suffix?: React.ReactNode;
}) => {
  const titleElement =
    typeof children === 'string' ? (
      <h1 className={cn('text-text-primary font-heading-3xl font-semibold truncate', className)}>{children}</h1>
    ) : (
      children
    );

  if (!suffix) return titleElement;

  return (
    <div className="flex items-center gap-4">
      {titleElement}

      {typeof suffix === 'string' ? <span className="text-text-tertiary font-body-sm">{suffix}</span> : suffix}
    </div>
  );
};

const PageHeaderDescription = ({ description, className }: { description: React.ReactNode; className?: string }) => {
  return <p className={cn('text-text-tertiary font-body-sm', className)}>{description}</p>;
};

const PageHeaderBase = ({
  title,
  titleClassName,
  titleSuffix,
  className,
  left,
  right,
  description,
  descriptionClassName,
  ...rest
}: PageHeaderProps) => {
  return (
    <div
      className={cn('p-6', className)}
      style={{
        display: 'grid',
        gridTemplateAreas: left
          ? `'left title . right' 'left description description description'`
          : `'title . right' 'description description description'`,
        gridTemplateColumns: left ? 'auto auto 1fr auto' : 'auto 1fr auto',
        columnGap: 16,
        alignItems: 'center',
      }}
      {...rest}
    >
      {left && (
        <div style={{ gridArea: 'left' }} className="w-fit">
          {left}
        </div>
      )}

      <div style={{ gridArea: 'title' }} data-slot="title">
        <PageHeaderTitle className={titleClassName} suffix={titleSuffix}>
          {title}
        </PageHeaderTitle>
      </div>

      {description && (
        <div style={{ gridArea: 'description' }} data-slot="description">
          <PageHeaderDescription description={description} className={descriptionClassName} />
        </div>
      )}

      {right && <div style={{ gridArea: 'right' }}>{right}</div>}
    </div>
  );
};

export const PageHeader = memo(PageHeaderBase);
